export type PublicationRestrictionState = 'none' | 'timed' | 'manual_hold';
export type PublicationRestriction = {
  state: PublicationRestrictionState;
  restricted_until: string | null;
  manual_hold: boolean;
  active_violation_count: number;
  revision: number;
  latest_decision_id: string | null;
  server_time?: string;
};
export type ModerationViolationSummary = {
  id: string;
  confirmed_at: string | null;
  venue: { id: string; name: string | null };
  status: 'active' | 'overturned';
  reason_code: string;
  reason_label: string;
  severity: 'ordinary' | 'serious' | 'critical';
  severity_label: string;
  public_explanation: string;
  penalty_points: number;
};
export type ModerationViolationDetail = ModerationViolationSummary & {
  global_xp_delta: number;
  local_xp_delta: number;
  reversal_xp_delta: number;
  net_xp_delta: number;
  applied_restriction: Record<string, unknown>;
  current_restriction: PublicationRestriction;
  overturn: null | { at: string | null; explanation: string | null };
  content_restoration: string | null;
};
export type XpTransaction = {
  id: string;
  occurred_at: string | null;
  type: string;
  points: number;
  description: string;
  venue: null | { id: string; name: string | null };
  visit_id: string | null;
  decision_id: string | null;
  reversal_of_id: string | null;
};
export type CursorPage<T> = { data: T[]; meta: { next_cursor: string | null; has_more: boolean } };

type ApiError = { error?: { code?: string; message?: string } };
export class ModerationApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(status: number, payload: ApiError | null) {
    super(payload?.error?.message ?? `Moderation API failed: ${status}`);
    this.status = status;
    this.code = payload?.error?.code ?? null;
  }
}

async function privateGet<T>(url: string): Promise<T> {
  const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  const payload = await response.json().catch(() => null) as T & ApiError | null;
  if (!response.ok) throw new ModerationApiError(response.status, payload);
  return payload as T;
}

export function fetchModerationState(): Promise<{ data: PublicationRestriction }> { return privateGet('/api/v1/me/moderation'); }
export function fetchModerationViolations(cursor: string | null = null): Promise<CursorPage<ModerationViolationSummary>> {
  const query = new URLSearchParams({ limit: '20' }); if (cursor) query.set('cursor', cursor);
  return privateGet(`/api/v1/me/moderation/violations?${query.toString()}`);
}
export function fetchModerationViolation(id: string): Promise<{ data: ModerationViolationDetail }> { return privateGet(`/api/v1/me/moderation/violations/${encodeURIComponent(id)}`); }
export function fetchXpTransactions(cursor: string | null = null, venueId: string | null = null): Promise<CursorPage<XpTransaction>> {
  const query = new URLSearchParams({ limit: '20' }); if (cursor) query.set('cursor', cursor); if (venueId) query.set('venue_id', venueId);
  return privateGet(`/api/v1/me/xp/transactions?${query.toString()}`);
}
